import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Tooltip } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { usePublicRating, identifiedPlayer } from './PublicRatingScope'
import './publicRatings.css'
export { PublicRatingScope, PlayerRatingScope, entryUser } from './PublicRatingScope'

let nextId = 0
export const publishedReputation = value => value?.scope === 'global' && value.status === 'published'
  && Number.isFinite(value.avgRating) && value.avgRating >= 1 && value.avgRating <= 5
  && Number.isSafeInteger(value.totalRatings) && value.totalRatings >= 3
const publishedExperience = row => Number.isFinite(row?.average) && row.average >= 1 && row.average <= 5
  && (row.source === 'current' ? Number.isSafeInteger(row.count) && row.count > 0
    : row.source === 'organizer-history' && Number.isSafeInteger(row.eventCount) && row.eventCount > 0)
// An event shows only its own reviews. For an event without them the API falls back
// to the organizer's history, which is not the tournament's rating: CP shows none.
const publishedEventReview = row => row?.source === 'current' && publishedExperience(row)

// The DS yellow star (#FFCD1A) CP draws beside a rating, never a text „★” (tasks/lessons.md, 2026-10-21).
export function RatingStar({ published }) {
  return <svg className={`cp-public-star${published ? '' : ' cp-public-star-empty'}`} viewBox="0 0 24 24" aria-hidden="true"
    fill={published ? 'currentColor' : 'none'} stroke={published ? 'none' : 'currentColor'} strokeWidth="1">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
}
export function RatingBadge({ value, label, explanation, children }) {
  const [id] = useState(() => `cp-public-rating-${++nextId}`)
  const [open, setOpen] = useState(false)
  return <span className="cp-public-rating">
    <button id={id} type="button" className="cp-public-rating-trigger" aria-label={`${label}: ${value == null ? explanation : value.toFixed(1)}`}
      onClick={event => event.stopPropagation()}
      onKeyDown={event => { if (event.key === 'Escape') setOpen(false) }} aria-describedby={open ? `${id}-info` : undefined}>
      <RatingStar published={value != null} /><strong>{value == null ? '—' : value.toFixed(1)}</strong>
    </button>
    {children && <span className="cp-public-rating-description">{children}</span>}
    <Tooltip id={`${id}-info`} target={id} isOpen={open} toggle={() => setOpen(current => !current)} trigger="hover focus click" placement="top">{explanation}</Tooltip>
  </span>
}
function useRatingText() {
  const { t } = useTranslation('reviewAudit')
  return { t, empty: status => t(status === 'error' ? 'ratingUnavailable' : status === 'loading' ? 'ratingLoading' : 'ratingNotPublished') }
}
export function PlayerIdentity({ player, to, className = '', children, linkProps, interactive = true }) {
  const { status, rows } = usePublicRating('players', player?.userId)
  const { t, empty } = useRatingText()
  const summary = rows.find(publishedReputation)
  const name = children ?? ([player?.firstName, player?.lastName].filter(Boolean).join(' ') || '—')
  const destination = to !== undefined ? to : (player?.id || player?._id ? `/accounts/players/${player.id || player._id}` : null)
  const explanation = summary ? t('reviewerCount', { count: summary.totalRatings }) : empty(status)
  return <span className="cp-player-identity">
    {identifiedPlayer(player) && (interactive ? <RatingBadge value={summary?.avgRating} label={t('playerReputation')} explanation={explanation} />
      : <span className="cp-public-rating" title={explanation} aria-label={`${t('playerReputation')}: ${summary ? summary.avgRating.toFixed(1) : explanation}`}><RatingStar published={!!summary} /><strong>{summary ? summary.avgRating.toFixed(1) : '—'}</strong></span>)}
    {destination ? <Link {...linkProps} className={className} to={destination}>{name}</Link> : <span className={className}>{name}</span>}
  </span>
}
// Native options only support text; the selected participant also has the
// normal identity below the control. Never embed interactive markup in option.
export function NativePlayerOption({ player, children, ...props }) {
  const { rows } = usePublicRating('players', player?.userId)
  const summary = rows.find(publishedReputation)
  return <option {...props}>{identifiedPlayer(player) ? `${summary ? '★' : '☆'} ${summary ? summary.avgRating.toFixed(1) : '—'} · ` : ''}{children}</option>
}
export function EventRating({ id }) {
  const { status, rows } = usePublicRating('events', id)
  const { t, empty } = useRatingText()
  const row = rows.find(publishedEventReview)
  const source = row ? t('currentEventReviews', { count: row.count }) : empty(status)
  return <RatingBadge value={row?.average} label={t('eventRating')} explanation={row ? t('currentEventRatingHelp') : source}>{source}</RatingBadge>
}
export function OrganizerRating({ id }) {
  const { status, rows } = usePublicRating('organizers', id)
  const { t, empty } = useRatingText()
  const published = rows.filter(row => publishedExperience(row) && ['tennis', 'padel'].includes(row.sportType))
  return <div className="cp-organizer-ratings">{published.length ? published.map(row => <RatingBadge key={row.sportType} value={row.average} label={t('organizerRating')}
    explanation={t('organizerRatingHelp')}>{t(row.sportType)} · {t('organizerHistoryReviews', { count: row.count, events: row.eventCount })}</RatingBadge>)
    : <RatingBadge label={t('organizerRating')} explanation={empty(status)}>{empty(status)}</RatingBadge>}</div>
}
export function ReputationStars({ value }) {
  const { t } = useTranslation('reviewAudit')
  if (!publishedReputation(value)) return <RatingBadge label={t('playerReputation')} explanation={t('ratingNotPublished')}>{t('ratingNotPublished')}</RatingBadge>
  return <span className="cp-reputation-summary" aria-label={`${t('playerReputation')}: ${value.avgRating.toFixed(1)}/5`}>
    <span className="cp-reputation-stars" aria-hidden="true">{[0, 1, 2, 3, 4].map(index => <span className="cp-reputation-star" key={index}>★<span style={{ width: `${Math.max(0, Math.min(1, value.avgRating - index)) * 100}%` }}>★</span></span>)}</span>
    <strong>{value.avgRating.toFixed(1)}</strong><span>· {t('reviewerCount', { count: value.totalRatings })}</span>
  </span>
}
